import React, { useEffect, useRef, useState } from 'react';

const LogoPatternBackground = ({ className, opacity = 0.02, size = 250, animate = true }) => {
  const canvasRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Mobil cihaz tespiti
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Mobilde animasyonu devre dışı bırak
  const shouldAnimate = animate && !isMobile;
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const images = {
      guitar: new Image(),
      piano: new Image(),
      trumpet: new Image(),
      microphone: new Image()
    };
    
    // SVG dosyalarını yükle
    images.guitar.src = '/guitar.svg';
    images.piano.src = '/piano.svg';
    images.trumpet.src = '/trumpet.svg';
    images.microphone.src = '/microphone.svg';
    
    // İkon çiz
    const drawIcon = (iconName, x, y, size) => {
      if (images[iconName].complete) {
        ctx.drawImage(images[iconName], x, y, size, size);
      } else {
        images[iconName].onload = () => {
          ctx.drawImage(images[iconName], x, y, size, size);
        };
      }
    };
    
    // Daha düzenli arkaplan için grid deseni çiz
    const drawGridPattern = () => {
      if (!canvas) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = opacity;
      
      // Izgara deseni boyutları
      const gridSize = size * 1.5; // Daha geniş aralıklar için
      
      // Kaç satır ve sütun olacağını hesapla
      const cols = Math.ceil(canvas.width / gridSize) + 1;
      const rows = Math.ceil(canvas.height / gridSize) + 1;
      
      // İkonların boyutu (gridSize'dan küçük olmalı)
      const iconSize = gridSize * 0.4;
      
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          // Her hücrenin orta noktası
          const centerX = i * gridSize;
          const centerY = j * gridSize;
          
          // Her satır için farklı desenler kullan (daha düzenli görünüm için)
          const pattern = j % 4;
          
          // İkon tipi ve rotasyonu satır ve sütun konumuna göre belirle
          let iconType, rotation;
          
          switch (pattern) {
            case 0:
              // İlk satır: gitar
              iconType = 'guitar';
              rotation = (i % 2) ? 0 : 15; // Alternatif hücrelerde farklı rotasyonlar
              break;
            case 1:
              // İkinci satır: piyano
              iconType = 'piano';
              rotation = (i % 2) ? 15 : 0;
              break;
            case 2:
              // Üçüncü satır: mikrofon
              iconType = 'microphone';
              rotation = (i % 2) ? 0 : 15;
              break;
            case 3:
              // Dördüncü satır: trompet
              iconType = 'trumpet';
              rotation = (i % 2) ? 15 : 0;
              break;
          }
          
          // Animasyon efekti (çok hafif)
          if (shouldAnimate) {
            const time = Date.now() / 10000;
            // Her ikon için çok hafif ve farklı bir dönüş (fark edilmeyecek kadar az)
            const animationAngle = Math.sin(time + (i + j) * 0.1) * 2; // ±2 derece
            rotation += animationAngle;
          }
          
          // İkonu çiz
          ctx.save();
          ctx.translate(centerX + iconSize/2, centerY + iconSize/2);
          ctx.rotate((rotation * Math.PI) / 180);
          drawIcon(iconType, -iconSize/2, -iconSize/2, iconSize);
          ctx.restore();
        }
      }
      
      if (shouldAnimate) {
        // Çok yavaş bir animasyon için yeterli
        requestAnimationFrame(() => {
          setTimeout(drawGridPattern, 100); // 100ms gecikme (CPU kullanımını azaltmak için)
        });
      }
    };
    
    // Canvas boyutlarını ayarla
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      if (!shouldAnimate) {
        // Animasyon yoksa sadece bir kez çiz
        drawGridPattern();
      }
    };
    
    // Pencere boyutu değiştiğinde canvas'ı yeniden boyutlandır
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    
    // Animasyon referansı
    let animationFrame;
    
    // Tüm görseller yüklendiğinde deseni çiz
    const checkAllImagesLoaded = () => {
      const allLoaded = Object.values(images).every(img => img.complete);
      
      if (allLoaded) {
        // Görseller yüklendi, deseni çiz
        if (shouldAnimate) {
          animationFrame = requestAnimationFrame(drawGridPattern);
        } else {
          drawGridPattern();
        }
      } else {
        // Yüklenene kadar bekle
        setTimeout(checkAllImagesLoaded, 50);
      }
    };
    
    checkAllImagesLoaded();
    
    // Temizleme
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [opacity, size, shouldAnimate, isMobile]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className={`absolute inset-0 pointer-events-none ${className || ''}`}
    />
  );
};

export default LogoPatternBackground;